// ─── Filter Types ────────────────────────────────────────────────────

export enum AssetReportFilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  IN = 'in',
  NOT_IN = 'not_in',
  GREATER_THAN = 'greater_than',
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN = 'less_than',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
  BETWEEN = 'between',
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',
  IS_EMPTY = 'is_empty',
  IS_NOT_EMPTY = 'is_not_empty',
}

export enum AssetReportLogicalOperator {
  AND = 'and',
  OR = 'or',
}

export interface AssetFilterCondition {
  id?: string;
  field: string;
  operator: AssetReportFilterOperator;
  value: any;
  valueEnd?: any;
}

export interface AssetFilterGroup {
  id?: string;
  logicalOperator: AssetReportLogicalOperator;
  conditions: (AssetFilterCondition | AssetFilterGroup)[];
}

export interface AssetFilterCriteria {
  version: '1.0';
  root: AssetFilterGroup;
}

export function isAssetFilterGroup(obj: any): obj is AssetFilterGroup {
  return obj && 'logicalOperator' in obj && 'conditions' in obj;
}

// ─── Report Configuration Types ─────────────────────────────────────

export interface ReportDimension {
  field: string;
  label: string;
  dateGrouping?: 'day' | 'week' | 'month' | 'quarter' | 'year';
}

export interface ReportMeasure {
  field: string;
  aggregation: 'count' | 'sum' | 'avg' | 'min' | 'max';
  label: string;
}

export type ReportViewType = 'pivot' | 'chart';
export type ChartType = 'bar' | 'stacked_bar' | 'line' | 'pie' | 'doughnut';

// ─── Field Metadata ─────────────────────────────────────────────────

export type ReportFieldType = 'string' | 'number' | 'boolean' | 'enum' | 'date' | 'relation';
export type ReportFieldCategory = 'core' | 'relation' | 'date' | 'dynamic';

export interface ReportFieldMetadata {
  field: string;
  label: string;
  type: ReportFieldType;
  category: ReportFieldCategory;
  enumValues?: { key: string; label: string }[];
  operators: AssetReportFilterOperator[];
  supportsMeasure?: boolean;
  categoryId?: number;
}

// ─── Response Types ─────────────────────────────────────────────────

export interface PivotRow {
  rowKey: Record<string, string>;
  columns: Record<string, Record<string, number>>;
  totals: Record<string, number>;
}

export interface PivotReportResponse {
  rows: PivotRow[];
  columnKeys: string[];
  grandTotals: Record<string, number>;
}

export interface ChartReportResponse {
  labels: string[];
  datasets: { label: string; data: number[] }[];
}

// ─── Saved View ─────────────────────────────────────────────────────

export interface AssetReportView {
  id: number;
  name: string;
  createdByClerkId: string;
  isShared: boolean;
  viewType: ReportViewType;
  chartType: ChartType | null;
  rowDimensions: ReportDimension[];
  columnDimensions: ReportDimension[];
  measures: ReportMeasure[];
  filterCriteria: AssetFilterCriteria | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ─── Report State ───────────────────────────────────────────────────

export interface ReportConfig {
  viewType: ReportViewType;
  chartType: ChartType;
  rowDimensions: ReportDimension[];
  columnDimensions: ReportDimension[];
  measures: ReportMeasure[];
  filterCriteria: AssetFilterCriteria | null;
}
